"use client";
import Checkbox from "@/components/atoms/checkbox";
import Accordion from "@/components/molecules/accordion";
import React from "react";
import Drawer from "../../drawer";
import useDisclosure from "@/hooks/useDisclosure";
import { AdjustmentsHorizontalIcon as FiltersIcon } from '@heroicons/react/24/outline';
import Sort from "./sort";
import ActiveFilters from "./active-filters";
import Button from "@/components/atoms/button";


type Filters = {
    category: string[];
    type: string[];
    sortBy?: string;
};

type DesktopFiltersProps = {
    categories: string[];
    types: string[];
    filters: Filters;
    onToggleFilter: (name: 'category' | 'type', value: string, checked: boolean) => void;
    onClear: () => void;
    value?: string;
    options?: { value: string; label: string }[];
    onChange?: (value: string) => void;
    filter?: any;
    filteredCount?: any;
    onClearAll?: any;
    countsByCategory?: Record<string, number>;
    countsByType?: Record<string, number>;
};

const DesktopFilters: React.FC<DesktopFiltersProps> = ({
    categories,
    types,
    filters,
    onToggleFilter,
    onClear,
    value,
    options,
    onChange,
    filter,
    filteredCount,
    onClearAll,
    countsByCategory,
    countsByType,
}) => {
    const handleCheckboxChange = (
        checked: boolean,
        name: 'category' | 'type',
        value: string
    ) => {
        onToggleFilter(name, value, checked);
    };

    const { isOpen, onOpen, onClose } = useDisclosure();

    const [allExpanded, setAllExpanded] = React.useState<boolean | null>(null);
    const [categoryExpanded, setCategoryExpanded] = React.useState(false);
    const [typeExpanded, setTypeExpanded] = React.useState(false);

    const releaseAndKeepOther = React.useCallback((accordion: 'category' | 'type', newExpanded: boolean) => {
        setAllExpanded(null);
        const other = allExpanded ?? false;
        if (accordion === 'category') {
            setCategoryExpanded(newExpanded);
            setTypeExpanded(other);
        } else {
            setTypeExpanded(newExpanded);
            setCategoryExpanded(other);
        }
    }, [allExpanded]);

    const controlled = allExpanded !== null;
    const bothExpanded = controlled ? allExpanded : (categoryExpanded && typeExpanded);
    const visibleCategories = React.useMemo(
        () => categories.filter((cat) => (countsByCategory?.[cat] ?? 0) > 0),
        [categories, countsByCategory]
    );
    const visibleTypes = React.useMemo(
        () => types.filter((type) => (countsByType?.[type] ?? 0) > 0),
        [types, countsByType]
    );
    const categoryProps = controlled
        ? { isExpanded: allExpanded, onExpand: () => releaseAndKeepOther('category', true), onCollapse: () => releaseAndKeepOther('category', false) }
        : { isExpanded: categoryExpanded, onExpand: () => setCategoryExpanded(true), onCollapse: () => setCategoryExpanded(false) };
    const typeProps = controlled
        ? { isExpanded: allExpanded, onExpand: () => releaseAndKeepOther('type', true), onCollapse: () => releaseAndKeepOther('type', false) }
        : { isExpanded: typeExpanded, onExpand: () => setTypeExpanded(true), onCollapse: () => setTypeExpanded(false) };

    return (
        <>
            <div className="hidden lg:block lg:w-[15rem]  ">
                <div className="sticky top-4">

                    <div className="hidden items-center justify-between pt-4 lg:flex lg:mt-[45px] xl:grow border-b border-neutral-500 py-6">
                        <div className="text-32 font-medium leading-8 text-[#333333]">Filter</div>
                        <div
                            className="flex h-[32px] gap-2 cursor-pointer items-center justify-center px-2 transition"

                        >
                            <span
                                className="ml-4 text-16 font-normal leading-6 text-[#333333] underline"
                                onClick={() =>
                                    bothExpanded
                                        ? (controlled ? setAllExpanded(false) : (setCategoryExpanded(false), setTypeExpanded(false)))
                                        : (controlled ? setAllExpanded(true) : (setCategoryExpanded(true), setTypeExpanded(true)))
                                }
                            >
                                {bothExpanded ? 'Collapse All' : 'Expand All'}
                            </span>
                            <span className="text-16 font-normal leading-6 text-[#333333] underline" onClick={onClear}>
                                Clear All
                            </span> 
                        </div>
                    </div>

                    <div className="">
                        {/* CATEGORY ACCORDION */}
                        <div className="!border-b border-neutral-500 py-3">
                            <Accordion className="border-none" {...categoryProps}>
                                <Accordion.Button className=" !px-0">
                                    Category
                                </Accordion.Button>
                                <Accordion.Panel className="!px-0">
                                    <div className="flex flex-col gap-6 py-2">
                                        {visibleCategories.map((cat) => (
                                            <Checkbox
                                                key={cat}
                                                checked={Array.isArray(filters.category) ? filters.category.includes(cat) : false}
                                                onChecked={(checked) =>
                                                    handleCheckboxChange(checked, "category", cat)
                                                }
                                                label={
                                                    typeof countsByCategory?.[cat] === 'number'
                                                        ? `${cat} (${countsByCategory[cat]})`
                                                        : cat
                                                }
                                            />
                                        ))}
                                    </div>
                                </Accordion.Panel>
                            </Accordion>
                        </div>
                        {/* TYPE ACCORDION */}
                        <div className="!border-b border-neutral-500 py-3">
                            <Accordion className="border-none" {...typeProps}>
                                <Accordion.Button className="!px-0">
                                    Type
                                </Accordion.Button>
                                <Accordion.Panel className="!px-0">
                                    <div className="flex flex-col gap-6 py-2">
                                        {visibleTypes.map((type) => (
                                            <Checkbox
                                                key={type}
                                                checked={Array.isArray(filters.type) ? filters.type.includes(type) : false}
                                                onChecked={(checked) =>
                                                    handleCheckboxChange(checked, "type", type)
                                                }
                                                label={
                                                    typeof countsByType?.[type] === 'number'
                                                        ? `${type} (${countsByType[type]})`
                                                        : type
                                                }
                                                className="text-blue-600 focus:ring-blue-500"
                                            />
                                        ))}
                                    </div>
                                </Accordion.Panel>
                            </Accordion>
                        </div>
                    </div>
                </div>
            </div>
            {/* Mobile Filters */}
            <div className="block lg:hidden ">
                <div className="py-4 px-4">
                    <ActiveFilters
                        filters={filter}
                        filteredCount={filteredCount}
                        onClear={onClearAll}
                        countsByCategory={countsByCategory}
                        countsByType={countsByType}
                    />
                </div>
                <div className="mt-5 flex gap-3 p-4  justify-center">
                    <button
                        className="flex grow cursor-pointer h-[44px] items-center justify-center gap-2 border border-[#2b2c39] p-2 leading-6 font-medium text-[#090909] transition md:w-fit lg:grow-0"
                        onClick={onOpen}
                    >
                        <FiltersIcon width={20} />
                        <span>Filter</span>

                    </button>
                    {options && onChange ? (
                        <Sort
                            value={value ?? ""}
                            options={options}
                            onChange={onChange}
                        />
                    ) : null}
                    {/* <SortDropdown setLoading={setLoading} />
        <ViewToggle /> */}
                </div>

                <Drawer
                    isOpen={isOpen}
                    onClose={onClose}
                    direction="bottom"
                    headline={'Filter'}
                    className="w-full"
                    blockScrolling={true}
                    headerClassName="border-y border-neutral-400 px-10"
                    drawerWrapperClassName1=' !fixed left-0  bottom-[62px] lg:bottom-0 w-full h-100'
                    onClearAll={onClear}
                    allExpanded={bothExpanded}
                    onToggleExpandAll={() =>
                        bothExpanded
                            ? (controlled ? setAllExpanded(false) : (setCategoryExpanded(false), setTypeExpanded(false)))
                            : (controlled ? setAllExpanded(true) : (setCategoryExpanded(true), setTypeExpanded(true)))
                    }
                // optionalData={<CurrentRefinements/>}
                >
                    <div className="flex h-full flex-col">
                        <div className="grow overflow-y-auto px-4 lg:px-5">
                            {/* <Facets setLoading={setLoading} /> */}
                            <div className="">
                                {/* CATEGORY ACCORDION */}
                                <div className="!border-b border-neutral-500 py-3 px-6">
                                    <Accordion className="border-none" {...categoryProps}>
                                        <Accordion.Button className=" !px-0">
                                            Category
                                        </Accordion.Button>
                                        <Accordion.Panel className="!px-0">
                                            <div className="flex flex-col gap-6 py-2">
                                                {visibleCategories.map((cat) => (
                                                    <Checkbox
                                                        key={cat}
                                                        checked={Array.isArray(filters.category) ? filters.category.includes(cat) : false}
                                                        onChecked={(checked) =>
                                                            handleCheckboxChange(checked, "category", cat)
                                                        }
                                                        label={
                                                            typeof countsByCategory?.[cat] === 'number'
                                                                ? `${cat} (${countsByCategory[cat]})`
                                                                : cat
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Accordion.Panel>
                                    </Accordion>
                                </div>
                                {/* TYPE ACCORDION */}
                                <div className="!border-b border-neutral-500 py-3 px-6">
                                    <Accordion className="border-none" {...typeProps}>
                                        <Accordion.Button className="!px-0">
                                            Type
                                        </Accordion.Button>
                                        <Accordion.Panel className="!px-0">
                                            <div className="flex flex-col gap-6 py-2">
                                                {visibleTypes.map((type) => (
                                                    <Checkbox
                                                        key={type}
                                                        checked={Array.isArray(filters.type) ? filters.type.includes(type) : false}
                                                        onChecked={(checked) =>
                                                            handleCheckboxChange(checked, "type", type)
                                                        }
                                                        label={
                                                            typeof countsByType?.[type] === 'number'
                                                                ? `${type} (${countsByType[type]})`
                                                                : type
                                                        }
                                                        className="text-blue-600 focus:ring-blue-500"
                                                    />
                                                ))}
                                            </div>
                                        </Accordion.Panel>
                                    </Accordion>
                                </div>
                                
                                {/* Apply Filter Button positioned in middle */}
                                <div className="px-6 py-6 text-center">
                                    <Button 
                                        variant='ghost' 
                                        className="border border-[#2B2C39] text-white !bg-[#2B2C39] px-9 py-3 text-16 font-medium" 
                                        onClick={onClose}
                                    >
                                        Apply Filter
                                    </Button>
                                </div>
                                
                            </div>
                        </div>
                        
                    </div>
                </Drawer>
            </div>
            {/* Mobile Filters */}

        </>
    );
};

export default DesktopFilters;
